import React from "react";
import { Helmet } from "react-helmet";
import { Typography } from "@mui/material";
import Footer from "../Components/Footer";
import { LegalLayout, Section, Paragraph, BulletList, LegalPageLink } from "../Components/Legal/LegalLayout";

export default function PrivacyPolicy() {
  return (
    <div>
      <Helmet>
        <title>Privacy Policy | GroHair & GloSkin Dindigul</title>
        <meta
          name="description"
          content="Read the Privacy Policy of GroHair & GloSkin Dindigul to understand how we collect, use and protect your personal information."
        />
        <meta property="og:title" content="Privacy Policy | GroHair & GloSkin Dindigul" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://adgrohairgloskindindigul.in/privacy-policy" />
      </Helmet>

      <LegalLayout
        title="Privacy Policy"
        lastUpdated="August 28, 2026"
        intro={
          <>
            This Privacy Policy describes Our policies and procedures on the collection, use and
            disclosure of Your information when You use the Service and tells You about Your
            privacy rights and how the law protects You.
            <br />
            <br />
            We use Your Personal Data to provide and improve the Service. We collect, use, and
            disclose Your information as described in this Privacy Policy and, where required by
            applicable law, only where We have a valid legal basis to do so, including Your consent
            (where consent is required).
          </>
        }
      >
        <Section heading="Interpretation and Definitions">
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
            Interpretation
          </Typography>
          <Paragraph>
            The words whose initial letters are capitalized have meanings defined under the
            following conditions. The following definitions shall have the same meaning regardless
            of whether they appear in singular or in plural.
          </Paragraph>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
            Definitions
          </Typography>
          <Paragraph>For the purposes of this Privacy Policy:</Paragraph>
          <BulletList
            items={[
              <>
                <strong>Account</strong> means a unique account created for You to access Our
                Service or parts of Our Service.
              </>,
              <>
                <strong>Affiliate</strong> means an entity that controls, is controlled by, or is
                under common control with a party, where "control" means ownership of 50% or more of
                the shares, equity interest or other securities entitled to vote for election of
                directors or other managing authority.
              </>,
              <>
                <strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our"
                in this Privacy Policy) refers to Advanced GroHair & GloSkin – Dindigul, 1st floor,
                2, UTHCHAV MALL, 6, Trichy - Dindigul Rd, near City Hospital, Gandhiji Nagar,
                Cooperative Nagar, Dindigul, Tamil Nadu 624001.
              </>,
              <>
                <strong>Cookies</strong> are small files that are placed on Your computer, mobile
                device or any other device by a website, containing the details of Your browsing
                history on that website, among its many uses.
              </>,
              <>
                <strong>Country/State</strong> refers to: Tamil Nadu, India.
              </>,
              <>
                <strong>Device</strong> means any device that can access the Service, such as a
                computer, a cell phone or a digital tablet.
              </>,
              <>
                <strong>Personal Data</strong> (or "Personal Information") is any information that
                relates to an identified or identifiable individual. We use "Personal Data" and
                "Personal Information" interchangeably unless a law uses a specific term.
              </>,
              <>
                <strong>Service</strong> refers to the Website.
              </>,
              <>
                <strong>Service Provider</strong> means any natural or legal person who processes
                the data on behalf of the Company. It refers to third-party companies or individuals
                employed by the Company to facilitate the Service, to provide the Service on behalf
                of the Company, to perform services related to the Service or to assist the Company
                in analyzing how the Service is used.
              </>,
              <>
                <strong>Usage Data</strong> refers to data collected automatically, either generated
                by the use of the Service or from the Service infrastructure itself (for example,
                the duration of a page visit).
              </>,
              <>
                <strong>User</strong> means any individual who accesses or uses the Service.
              </>,
              <>
                <strong>Website</strong> refers to GroHair & GloSkin Dindigul, accessible from{" "}
                <a href="https://adgrohairgloskindindigul.in/" style={{ color: "#e3242b" }}>
                  https://adgrohairgloskindindigul.in/
                </a>
                .
              </>,
              <>
                <strong>You</strong> means the individual accessing or using the Service, or the
                company, or other legal entity on behalf of which such individual is accessing or
                using the Service, as applicable.
              </>,
            ]}
          />
        </Section>

        <Section heading="Collecting and Using Your Personal Information">
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
            Types of Data Collected
          </Typography>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>
            Personal Data
          </Typography>
          <Paragraph>
            While using Our Service, We may ask You to provide Us with certain personally
            identifiable information that can be used to contact or identify You. Personally
            identifiable information may include, but is not limited to:
          </Paragraph>
          <BulletList
            items={[
              "Email address",
              "First name and last name",
              "Phone number",
              "Address, State, Province, ZIP/Postal code, City",
            ]}
          />

          <Typography fontWeight="bold" sx={{ mb: 1 }}>
            Usage Data
          </Typography>
          <Paragraph>Usage Data is collected automatically when using the Service.</Paragraph>
          <Paragraph>
            Usage Data may include information such as Your Device's Internet Protocol address
            (e.g. IP address), browser type, browser version, the pages of Our Service that You
            visit, the time and date of Your visit, the time spent on those pages, unique device
            identifiers and other diagnostic data.
          </Paragraph>
          <Paragraph>
            When You access the Service by or through a mobile device, We may collect certain
            information automatically, including, but not limited to, the type of mobile device You
            use, Your mobile device's unique ID, the IP address of Your mobile device, Your mobile
            operating system, the type of mobile Internet browser You use, unique device identifiers
            and other diagnostic data.
          </Paragraph>
          <Paragraph>
            We may also collect information that Your browser sends whenever You visit Our Service
            or when You access the Service by or through a mobile device.
          </Paragraph>

          <Typography fontWeight="bold" sx={{ mb: 1 }}>
            Tracking Technologies and Cookies
          </Typography>
          <Paragraph>
            We use tracking technologies (such as cookies) to track the activity and to improve Our
            Service. The technologies We use may include:
          </Paragraph>
          <BulletList
            items={[
              <>
                <strong>Cookies or Browser Cookies.</strong> A cookie is a small file placed on Your
                Device. You can instruct Your browser to refuse all Cookies or to indicate when a
                Cookie is being sent. However, if You do not accept Cookies, You may not be able to
                use some parts of Our Service.
              </>,
              <>
                <strong>Web Beacons.</strong> Certain sections of Our Service may contain small
                electronic files known as web beacons (also referred to as clear gifs, pixel tags,
                and single-pixel gifs) that permit the Company, for example, to count users who have
                visited those pages and for other related website statistics.
              </>,
            ]}
          />
          <Paragraph>
            Cookies can be "Persistent" or "Session" Cookies. Persistent Cookies remain on Your
            personal computer or mobile device when You go offline, while Session Cookies are
            deleted as soon as You close Your web browser.
          </Paragraph>
          <Paragraph>
            Where required by law, We use non-essential cookies (that is, Cookies other than the
            Necessary / Essential Cookies described below) only with Your consent. You can withdraw
            or change Your consent at any time using Our cookie preferences banner or through Your
            browser/device settings. Withdrawing consent does not affect the lawfulness of
            processing based on consent before its withdrawal. For more detail on the specific
            cookies We use, see Our <LegalPageLink to="/cookie-policy">Cookie Policy</LegalPageLink>.
          </Paragraph>
        </Section>

        <Section heading="Use of Your Personal Data">
          <Paragraph>The Company may use Personal Data for the following purposes:</Paragraph>
          <BulletList
            items={[
              "To provide and maintain Our Service, including to monitor the usage of Our Service.",
              "To manage Your Account: to manage Your registration as a user of the Service.",
              "For the performance of a contract: the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased or of any other contract with Us through the Service.",
              "To contact You: by email, telephone calls, SMS, or other equivalent forms of electronic communication regarding updates or informative communications related to the functionalities, products or contracted services, including security updates.",
              "To provide You with news, special offers and general information about other goods, services and events which We offer that are similar to those that You have already purchased or inquired about, where permitted by applicable law and Your consent where required. You may opt out at any time using the unsubscribe link in any marketing communication We send or by contacting Us.",
              "To manage Your requests: to attend and manage Your requests to Us.",
              "For business transfers: to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets.",
              "For other purposes: such as data analysis, identifying usage trends, determining the effectiveness of Our promotional campaigns, and evaluating and improving Our Service, products, services, marketing and Your experience.",
            ]}
          />
          <Paragraph>We may share Your Personal Data in the following situations:</Paragraph>
          <BulletList
            items={[
              "With Service Providers: to monitor and analyze the use of Our Service, and to contact You.",
              "For business transfers: in connection with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition of all or a portion of Our business to another company.",
              "With Affiliates: We may share Your Personal Data with Our affiliates, in which case We will require those affiliates to honor this Privacy Policy.",
              "With other users: if Our Service offers public areas, information You share may be viewed by all users and may be publicly distributed.",
              "With Your consent: We may disclose Your Personal Data for any other purpose with Your consent.",
            ]}
          />
        </Section>

        <Section heading="Text Messages Privacy Notice">
          <Paragraph>
            You have the option to receive text (SMS) messages from Us. If You opt in to text
            messages, We will send You updates, notifications, and other communications. When You
            opt in, We will collect and store information such as Your phone number, the date and
            method of Your consent, and message delivery and read information.
          </Paragraph>
          <Paragraph>
            No mobile information will be shared with or sold to third parties or affiliates for
            marketing or promotional purposes. The phone numbers and consent records We collect for
            texting are never shared with anyone for any purpose, except the Service Providers that
            technically have to handle them to deliver the texts.
          </Paragraph>
          <Paragraph>
            Consent to receive text messages is not a condition of any purchase or use of Our
            Service. If You consent to receive SMS from Us, You agree to receive text messages
            related to customer care and support, appointment/account notifications and reminders,
            authentication messages, security alerts, and marketing offers. Reply STOP to opt-out.
            Reply HELP for support. Message & data rates may apply. Messaging frequency may vary.
          </Paragraph>
        </Section>

        <Section heading="Retention of Your Personal Data">
          <Paragraph>
            The Company will retain Your Personal Data only for as long as is necessary for the
            purposes set out in this Privacy Policy. We will retain and use Your Personal Data to
            the extent necessary to comply with Our legal obligations, resolve disputes, and enforce
            Our legal agreements and policies.
          </Paragraph>
          <Paragraph>
            Where possible, We apply shorter retention periods and/or reduce identifiability by
            deleting, aggregating, or anonymizing data. Unless otherwise stated, the retention
            periods below are maximum periods ("up to") and We may delete or anonymize data sooner
            when it is no longer needed for the relevant purpose.
          </Paragraph>
          <BulletList
            items={[
              "Account information: retained for the duration of Your Account relationship plus up to 24 months after account closure.",
              "Customer support data (tickets, correspondence, chat transcripts): up to 24 months from the date of ticket closure or interaction.",
              "Usage Data (website analytics, IP addresses, device identifiers, server logs): up to 24 months from the date of collection.",
            ]}
          />
          <Paragraph>
            We may retain Personal Data beyond these periods where necessary for a legal obligation,
            to establish or defend legal claims, at Your explicit request, or due to technical
            limitations such as backup systems scheduled for routine deletion.
          </Paragraph>
        </Section>

        <Section heading="Transfer of Your Personal Data">
          <Paragraph>
            Your information, including Personal Data, is processed at the Company's operating
            offices and in any other places where the parties involved in the processing are
            located. This means that this information may be transferred to — and maintained on —
            computers located outside of Your state, province, country or other governmental
            jurisdiction where the data protection laws may differ from those of Your jurisdiction.
          </Paragraph>
          <Paragraph>
            Where required by applicable law, We will ensure that international transfers of Your
            Personal Data are subject to appropriate safeguards. The Company will take all steps
            reasonably necessary to ensure that Your data is treated securely and in accordance with
            this Privacy Policy.
          </Paragraph>
        </Section>

        <Section heading="Delete Your Personal Data">
          <Paragraph>
            You have the right to delete or request that We assist in deleting the Personal Data
            that We have collected about You. You may contact Us at any time to request access to,
            correct, or delete any Personal Data that You have provided to Us. Please note, however,
            that We may need to retain certain information when We have a legal obligation or lawful
            basis to do so.
          </Paragraph>
        </Section>

        <Section heading="Disclosure of Your Personal Data">
          <Typography fontWeight="bold" sx={{ mb: 1 }}>Business Transactions</Typography>
          <Paragraph>
            If the Company is involved in a merger, acquisition or asset sale, Your Personal Data
            may be transferred. We will provide notice before Your Personal Data is transferred and
            becomes subject to a different Privacy Policy.
          </Paragraph>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>Law Enforcement</Typography>
          <Paragraph>
            Under certain circumstances, the Company may disclose Your Personal Data if required to
            do so by law or in response to valid requests by public authorities.
          </Paragraph>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>Other Legal Requirements</Typography>
          <Paragraph>
            The Company may disclose Your Personal Data in the good-faith belief that such action is
            necessary to comply with a legal obligation, protect and defend the rights or property
            of the Company, prevent or investigate possible wrongdoing in connection with the
            Service, protect the personal safety of Users of the Service or the public, or protect
            against legal liability.
          </Paragraph>
        </Section>

        <Section heading="Security of Your Personal Data">
          <Paragraph>
            The security of Your Personal Data is important to Us, but remember that no method of
            transmission over the Internet, or method of electronic storage, is 100% secure. While
            We strive to use commercially reasonable means to protect Your Personal Data, We cannot
            guarantee its absolute security.
          </Paragraph>
        </Section>

        <Section heading="Children's and Minors' Privacy">
          <Paragraph>
            The Service is not directed to, and We do not knowingly collect Personal Information
            from, anyone under the age of 16. If You are a parent or guardian and You believe Your
            child has provided Us with Personal Information, please contact Us. If We become aware
            that We have collected Personal Information from anyone under the age of 16, We will
            take steps to remove that information from Our servers as soon as reasonably possible.
          </Paragraph>
        </Section>

        <Section heading="Links to Other Websites">
          <Paragraph>
            Our Service may contain links to other websites that are not operated by Us. If You
            click on a third-party link, You will be directed to that third party's site. We
            strongly advise You to review the Privacy Policy of every site You visit. We have no
            control over and assume no responsibility for the content, privacy policies or practices
            of any third-party sites or services.
          </Paragraph>
        </Section>

        <Section heading="Changes to this Privacy Policy">
          <Paragraph>
            We may update Our Privacy Policy from time to time. We will notify You of any changes by
            posting the new Privacy Policy on this page and updating the "Last updated" date at the
            top of this Privacy Policy. You are advised to review this Privacy Policy periodically
            for any changes.
          </Paragraph>
        </Section>

        <Section heading="Contact Us">
          <Paragraph>
            If You have any questions about this Privacy Policy, You can contact Us:
          </Paragraph>
          <BulletList
            items={[
              "By email: adgrohairdgl@gmail.com",
              "By phone: +91 082701 56789",
            ]}
          />
        </Section>
      </LegalLayout>

      <Footer />
    </div>
  );
}
